import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { motion } from 'framer-motion';
import { Home, ChevronRight, Target, BookOpen, Globe, Heart, Award, Users, Lightbulb, Handshake, Cpu, Leaf, CheckCircle } from 'lucide-react';
import vmts from '../data/vmts-20260820.json';

const PageHero = ({ title, breadcrumbs }) => (
  <div className="page-hero pt-[70px]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
        <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
          <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
            <Home size={14} /> Beranda
          </Link>
          {breadcrumbs.map((bc, i) => (
            <React.Fragment key={i}>
              <ChevronRight size={14} />
              <span className={i === breadcrumbs.length - 1 ? 'text-white' : ''}>{bc}</span>
            </React.Fragment>
          ))}
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-white font-heading">{title}</h1>
      </motion.div>
    </div>
  </div>
);

const misiIcons = [BookOpen, Lightbulb, Heart, Award];
const misiItems = vmts.institution.misi.map((text, i) => ({ num: String(i + 1).padStart(2, '0'), icon: React.createElement(misiIcons[i], { size: 20 }), text }));
const tujuanIcons = [Users, Lightbulb, Target, Globe, Award, Cpu];
const tujuanTitles = ['Lulusan Profesional', 'Riset & Inovasi', 'Solusi Kesehatan', 'Pengabdian Berdampak', 'Tata Kelola Baik', 'Pusat Healthpreneurship'];
const tujuanItems = vmts.institution.tujuan.map((desc, i) => ({ icon: React.createElement(tujuanIcons[i], { size: 28 }), title: tujuanTitles[i], desc }));
const sasaranItems = vmts.institution.sasaran;

const VisiMisi = () => (
  <div data-testid="visi-misi-page">
    <SEO
      title="Visi, Misi & Tujuan"
      description="Visi dan Misi STIKES Harapan Ibu Jambi: menjadi perguruan tinggi kesehatan unggulan yang berkarakter, berdaya saing, dan berbasis healthpreneurship di Jambi."
    />
    <PageHero title="Visi, Misi, Tujuan & Sasaran" breadcrumbs={['Tentang Kami', 'Visi & Misi']} />

    <div className="bg-[#F8F7FF] dark:bg-[#121020]">

      {/* Visi */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="text-center mb-8">
              <span className="text-xs font-semibold text-purple-600 uppercase tracking-widest">VMTS STIKES HI</span>
              <h2 className="text-2xl font-bold text-[#1A1A2E] dark:text-white mt-2 font-heading">Visi Kami</h2>
            </div>
            <div
              data-testid="visi-card"
              className="relative rounded-3xl p-10 md:p-14 text-white overflow-hidden"
              style={{ background: 'linear-gradient(135deg, #4C1D95 0%, #7B2FBE 50%, #9333EA 100%)' }}
            >
              <div className="absolute top-4 left-6 text-white/10 text-[160px] font-serif leading-none select-none">"</div>
              <div className="relative z-10 text-center">
                <p className="text-xl md:text-2xl font-medium leading-relaxed italic">
                  "{vmts.institution.visi}"
                </p>
              </div>
              <div className="absolute bottom-4 right-6 text-white/10 text-[160px] font-serif leading-none select-none rotate-180">"</div>

              {/* Tiga pilar */}
              <div className="relative z-10 grid grid-cols-3 gap-4 mt-8">
                {[
                  { icon: <Cpu size={20} />, label: 'Teknologi' },
                  { icon: <Handshake size={20} />, label: 'Healthpreneurship' },
                  { icon: <Leaf size={20} />, label: 'Kearifan Lokal' },
                ].map((p, i) => (
                  <div key={i} className="text-center bg-white/10 rounded-xl py-3 px-2">
                    <div className="flex justify-center mb-1">{p.icon}</div>
                    <p className="text-xs font-semibold">{p.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Misi */}
      <section className="py-16 bg-white dark:bg-[#100E1C]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-[#1A1A2E] dark:text-white font-heading">Misi Kami</h2>
          </motion.div>
          <div className="space-y-4">
            {misiItems.map((misi, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="flex items-start gap-5 p-5 rounded-2xl bg-[#F8F7FF] dark:bg-[#1A182E] border-l-4 border-[#7B2FBE] hover:border-[#9333EA] transition-all"
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-[#7B2FBE] to-[#9333EA] flex items-center justify-center text-white font-bold text-sm">
                  {misi.num}
                </div>
                <div className="flex items-start gap-3 flex-1">
                  <span className="text-[#7B2FBE] dark:text-[#9333EA] mt-0.5 flex-shrink-0">{misi.icon}</span>
                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{misi.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tujuan */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-[#1A1A2E] dark:text-white font-heading">Tujuan</h2>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {tujuanItems.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="p-6 rounded-2xl bg-white dark:bg-[#1A182E] border border-purple-100 dark:border-purple-900/20 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center text-[#7B2FBE] mb-4">
                  {t.icon}
                </div>
                <h3 className="font-bold text-[#1A1A2E] dark:text-white mb-2 font-heading">{t.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{t.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sasaran */}
      <section className="py-16 bg-white dark:bg-[#100E1C]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-[#1A1A2E] dark:text-white font-heading">Sasaran</h2>
            <p className="text-gray-500 dark:text-gray-400 mt-3 text-sm max-w-2xl mx-auto">
              Target capaian strategis STIKES Harapan Ibu Jambi dalam mewujudkan visi dan misi institusi
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {sasaranItems.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="flex items-start gap-4 p-5 rounded-2xl bg-[#F8F7FF] dark:bg-[#1A182E] border border-purple-100 dark:border-purple-900/20 hover:shadow-md transition-all"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-[#7B2FBE] to-[#9333EA] flex items-center justify-center text-white text-xs font-bold">
                  {i + 1}
                </div>
                <div className="flex items-start gap-2 flex-1">
                  <CheckCircle size={16} className="text-purple-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{s}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Motto */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            data-testid="motto-section"
          >
            <p className="text-sm text-[#7B2FBE] font-semibold uppercase tracking-widest mb-4">Motto STIKES HI</p>
            <h2 className="text-4xl md:text-5xl font-bold font-heading italic leading-tight" style={{
              background: 'linear-gradient(135deg, #7B2FBE, #9333EA, #A855F7)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              "Unggul, Berkarakter, Berdaya Saing"
            </h2>
            <div className="mt-6 flex justify-center gap-3">
              <div className="h-0.5 w-16 bg-gradient-to-r from-transparent to-[#7B2FBE] rounded-full" />
              <div className="h-0.5 w-8 bg-[#7B2FBE] rounded-full" />
              <div className="h-0.5 w-16 bg-gradient-to-l from-transparent to-[#7B2FBE] rounded-full" />
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  </div>
);

export default VisiMisi;
